import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { resolveNativeReportGroups } from '../config/reportGroupAccess';
import { isEmulating, getOriginalUserSettings } from '../utils/emulation';
import { NAV_FLAGS, resolveNav, findTrail, flattenNav } from '../config/navConfig';

/**
 * Reads every PostHog flag the nav references. Hooks must run in a stable
 * order, so this is one call per entry of NAV_FLAGS (a constant array).
 */
function useNavFlags() {
  const values = NAV_FLAGS.map((flag) => [flag, useFeatureFlagEnabled(flag)]); // eslint-disable-line react-hooks/rules-of-hooks
  const signature = values.map(([, v]) => String(v)).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => Object.fromEntries(values), [signature]);
}

/**
 * The resolved desktop nav for the current user/org/flags, plus the active
 * breadcrumb trail and a flat page list for the command palette.
 */
export function useResolvedNav() {
  const { pathname } = useLocation();
  const flags = useNavFlags();
  const { userSettings, currentOrganization } = useUserSettingsContext();

  // Keyed by content, not object identity: every settings save replaces the
  // userSettings object, and a fresh `groups` array would rebuild `nav` and
  // re-render the whole sidebar for nothing.
  const resolvedGroups = resolveNativeReportGroups(currentOrganization, userSettings);
  const groupsSignature = Array.isArray(resolvedGroups) ? resolvedGroups.join('|') : String(resolvedGroups);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groups = useMemo(() => resolvedGroups, [groupsSignature]);

  // Admin is judged on the REAL user while emulating (AdminProtectedRoute
  // does the same), so an admin emulating a customer keeps the console.
  const emulating = isEmulating();
  const originalSettings = emulating ? getOriginalUserSettings() : null;
  const isAdmin = emulating && originalSettings
    ? originalSettings.isAdmin === 1
    : userSettings?.isAdmin === 1;

  // The old sidebar listed native report groups only once org access had
  // loaded — keep that so the list doesn't flash in unrestricted.
  const hasReportGrants = Boolean(currentOrganization && userSettings && userSettings.orgAccess);

  const nav = useMemo(
    () => resolveNav({ flags, groups, isAdmin, hasReportGrants }),
    [flags, groups, isAdmin, hasReportGrants]
  );
  const trail = useMemo(() => findTrail(nav, pathname), [nav, pathname]);
  const pages = useMemo(() => flattenNav(nav), [nav]);

  return { nav, trail, pages, pathname };
}

export default useResolvedNav;
